// ============================================================================
// ROLE-BASED FIELD FILTERING HOOK
// ============================================================================
//
// PURPOSE: Filter fields based on user roles using whitelist approach
//
// FEATURES:
// - Whitelist-based role filtering (allowedRoles only)
// - Works with SimpleFieldConfig interface
// - Memoized for performance
// - Type-safe role checking
//
// USAGE:
// - Import and use in table and form components
// - Provides filtered fields based on current user role
// - Automatically handles role-based field visibility
//
// ============================================================================

import { useMemo } from "react";
import { useRoleCheck } from "../auth/useRoleCheck";
import { SimpleFieldConfig } from "@/components/shared/UniversalCRMTable/types/shared";

export function useFieldFiltering() {
  const { roleName, isAdmin, isEmployee, isClientUser } = useRoleCheck();

  const filterFieldsByRole = useMemo(() => {
    return (fields: Record<string, SimpleFieldConfig>): Record<string, SimpleFieldConfig> => {
      const filteredFields: Record<string, SimpleFieldConfig> = {};

      Object.entries(fields).forEach(([fieldKey, fieldConfig]) => {
        // If no allowedRoles specified, field is visible to all roles
        if (!fieldConfig.allowedRoles || fieldConfig.allowedRoles.length === 0) {
          filteredFields[fieldKey] = fieldConfig;
          return;
        }

        // Check if current role is in the allowed roles list
        if (roleName && fieldConfig.allowedRoles.includes(roleName)) {
          filteredFields[fieldKey] = fieldConfig;
        }
      });

      return filteredFields;
    };
  }, [roleName]);

  const filterFieldsArrayByRole = useMemo(() => {
    return (fields: SimpleFieldConfig[]): SimpleFieldConfig[] => {
      return fields.filter((field) => {
        // If no allowedRoles specified, field is visible to all roles
        if (!field.allowedRoles || field.allowedRoles.length === 0) {
          return true;
        }

        // Check if current role is in the allowed roles list
        return roleName ? field.allowedRoles.includes(roleName) : false;
      });
    };
  }, [roleName]);

  return {
    filterFieldsByRole,
    filterFieldsArrayByRole,
    roleName,
    isAdmin,
    isEmployee,
    isClientUser,
  };
}
